<template>
    <div :class="{ backgroundGris: actualitesOpen || onboardingOpen }" @click="hideActusalites"></div>
    <Modal v-if="modaleBimOuverte" @cancel="modaleBimOuverte = false">
        <ModalCard
            :activeValidate="false"
            :withoutBotNav="true"
            headerTitle="B.I.M."
            @cancel="modaleBimOuverte = false"
        >
            <template #header-append>
                <ReglePastille cle="bim.definition" placement="bottom" />
            </template>
            <p>{{ modaleBimMessage }}</p>
        </ModalCard>
    </Modal>
    <div class="container">
        <MainHeader :connected="!isNoteReclamation" />
        <nav v-if="!isNoteReclamation && menuLoaded" :aria-label="$t('aria.menu.lateral')">
            <sidebar-menu
                :collapsed="isCollapsed"
                :hide-toggle="true"
                :menu="menu"
                :show-one-child="true"
                :width="'500px'"
                class="menu"
                @mouseenter="expandMenu"
                @mouseleave="collapseMenu"
                @item-click="onItemClick"
            >
                <template #header>
                    <div v-show="!isCollapsed" class="header-container">
                        <div class="title-container">{{ $t('menu.portail.title') }}</div>
                        <div class="pin-container">
                            <button
                                :class="{ 'is-pinned': isPinned }"
                                :title="isPinned ? $t('menu.portail.unpin') : $t('menu.portail.pin')"
                                class="pin-button"
                                @click="pinMenu"
                            >
                                <img :alt="$t('menu.portail.alt.pin')" :src="getPinIcon" />
                            </button>
                        </div>
                    </div>
                </template>
            </sidebar-menu>
        </nav>
        <Actualites v-if="!actualitesLoading && isDashboard" :actualites="actualites" class="actualites" />
        <main
            id="main-content"
            :class="{ 'content-dashboard': isDashboard || isDashboardFournisseur }"
            class="content"
            role="main"
        >
            <slot />
        </main>
        <Footer
            v-if="!isNoteReclamation && !isEditionDashboard && (isDashboard || isDashboardFournisseur)"
            :connected="!isNoteReclamation"
        />
    </div>
    <ReglesPanneau v-if="!isNoteReclamation && autoriseReglesGestion" />
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import MainHeader from '@/components/layout/MainHeader.vue';
import Footer from '@/components/layout/Footer.vue';
import Modal from '@/components/ui-components/Modal.vue';
import ModalCard from '@/components/ui-components/ModalCard.vue';
import { store } from '@/plugins/store';
import { HomeAction } from '@/modules/Home/store/actions-types';
import Actualites from '@/modules/Home/components/Actualites.vue';
import { Actualite } from '@/models/Actualite';
import 'vue-sidebar-menu/dist/vue-sidebar-menu.css';
import iconSwitchFournisseur from '../../assets/img/menu/menu.png';
import { AccountAction } from '@/modules/Account/store/actions-types';
import { updateMenuLabels } from '@/components/layout/store/state';
import i18n from '@/plugins/i18n';
import { HOME } from '@/modules/Home/store/mutation-types';
import LayoutService from '@/components/layout/LayoutService';
import SatisfactionService from '@/modules/Satisfaction/services/SatisfactionService';
import ReglesPanneau from '@/components/regles/ReglesPanneau.vue';
import ReglePastille from '@/components/regles/ReglePastille.vue';

export default defineComponent({
    name: 'Layout',
    components: { MainHeader, Footer, Actualites, Modal, ModalCard, ReglesPanneau, ReglePastille },
    data() {
        return {
            isCollapsed: true,
            menuLoaded: false,
            isPinned: false,
            modaleBimOuverte: false,
            modaleBimMessage: '',
            sharepointLink: '',
        };
    },
    computed: {
        actualitesOpen(): boolean {
            return this.$store.getters.getActualiteOpen;
        },
        getPinIcon(): string {
            if (this.isPinned) {
                return require('@/assets/img/unpin.svg');
            }
            return require('@/assets/img/pin.svg');
        },
        onboardingOpen(): boolean {
            return this.$store.getters['menuState/getOnBoardingOpen'];
        },
        actualites(): Actualite[] {
            return this.$store.getters.getActualites;
        },
        actualitesLoading(): boolean {
            return this.$store.getters.getActualiteLoading;
        },
        isDashboard(): boolean {
            return this.$router.currentRoute.value.fullPath === '/dashboard';
        },
        isDashboardFournisseur(): boolean {
            return this.$router.currentRoute.value.fullPath === '/dashboard-fournisseur';
        },
        isEditionDashboard(): boolean {
            return this.$router.currentRoute.value.fullPath === '/edition-dashboard';
        },
        isNoteReclamation(): boolean {
            return (
                this.$router.currentRoute.value.fullPath === '/noteReclamation' ||
                this.$router.currentRoute.value.name === 'note2' ||
                this.$router.currentRoute.value.path === '/validation-devis' ||
                this.$router.currentRoute.value.name === 'validationDevis' ||
                this.$router.currentRoute.value.path?.startsWith('/validation-devis')
            );
        },
        envActuel(): string {
            return window.CONFIG?.env || '';
        },
        envAutoriseReglesGestion(): boolean {
            return ['DEV', 'INT', 'REC'].includes(this.envActuel.toUpperCase());
        },
        autoriseReglesGestion(): boolean {
            return (
                this.envAutoriseReglesGestion &&
                !!this.$store.getters.getUserInformations?.fonctions?.includes('acces.reglesmetier')
            );
        },
        canConsultSharepoint(): boolean {
            return this.$store.getters.getUserInformations?.fonctions?.includes('consulter.lien.sharepoint');
        },
        menu(): any[] {
            const menu = store.getters['menuState/getMenuWithBim'];
            const satisfactionConditions = this.$store.getters.getSatisfactionConditions;
            if (!satisfactionConditions?.modalConditionDTO?.active) {
                return menu.filter((item: any) => item.href !== '/satisfaction');
            }
            return menu;
        },
    },
    mounted() {
        store.dispatch(HomeAction.GET_ACTUALITES);
    },
    async beforeMount() {
        if (this.canConsultSharepoint) {
            try {
                const response = await LayoutService.getSharepointLink();
                this.sharepointLink = response.data.url;
            } catch (error) {
                console.error('Erreur lors de la récupération du lien Sharepoint :', error);
            }
        }
        try {
            await Promise.all([
                this.$store.dispatch(HomeAction.GET_LANGUE_SELECTIONNEE),
                this.$store.dispatch(HomeAction.GET_TRADUCTIONS),
                this.$store.dispatch('menuState/GET_STATUT_BIM'),
            ]);

            updateMenuLabels();
        } catch (error) {
            console.error("Erreur lors de l'initialisation des traductions :", error);
        } finally {
            // On affiche le menu même si l'appel aux trads a échoué
            this.menuLoaded = true;
        }

        try {
            // Appels secondaires (Sociétés et Conditions de satisfaction)
            await this.$store.dispatch(AccountAction.GET_SOCIETES, false);

            if (this.$store.getters.getSatisfactionConditions == null) {
                const response = await SatisfactionService.getSatisfactionConditions();
                store.commit(HOME.SET_SATISFACTION_CONDITIONS, response);
            }

            // Ajout du bouton de switch de portail (Client/Fournisseur)
            const isFournisseur = localStorage.getItem('isFournisseur') === 'true';
            const isClient = localStorage.getItem('isClient') === 'true';

            if (this.canConsultSharepoint && this.sharepointLink) {
                const documentsMenu = this.menu.find((item: any) => item.title === i18n.global.t('menu.documents.parent'));
                if (documentsMenu && documentsMenu.child) {
                    const hasExternalLink = documentsMenu.child.some((child: any) => child.href === this.sharepointLink);
                    if (!hasExternalLink) {
                        documentsMenu.child.push({
                            title: i18n.global.t('menu.documents.externe'),
                            href: this.sharepointLink,
                            icon: 'none',
                        });
                    }
                }
            }

            const currentMenu = store.getters['menuState/getMenu'];

            if (
                isFournisseur &&
                isClient &&
                currentMenu.length > 0 &&
                !currentMenu[0].href.startsWith('/choix-portail')
            ) {
                const portailName = store.getters['authenticationState/getPortailName'];

                if (portailName === 'Client') {
                    this.menu.unshift({
                        href: '/choix-portail-fournisseur',
                        title: i18n.global.t('menu.portail.fournisseur'),
                        icon: {
                            element: 'img',
                            attributes: { src: iconSwitchFournisseur },
                        },
                    });
                } else {
                    this.menu.unshift({
                        href: '/choix-portail-client',
                        title: i18n.global.t('menu.portail.client') || 'Portail client',
                        icon: {
                            element: 'img',
                            attributes: { src: iconSwitchFournisseur },
                        },
                    });
                }
            }
        } catch (error) {
            console.error('Erreur lors du chargement des données complémentaires :', error);
        }
    },
    methods: {
        expandMenu() {
            this.isCollapsed = false;
        },
        collapseMenu() {
            if (!this.isPinned) {
                this.isCollapsed = true;
            }
        },
        pinMenu() {
            this.isPinned = !this.isPinned;
            if (this.isPinned) {
                this.isCollapsed = false;
            }
        },
        hideActusalites(): void {
            store.dispatch(HomeAction.SET_ACTUALITE_OPEN, false);
        },
        onItemClick(event: any, item: any) {
            this.isPinned = false;
            if (item.href === '/bim') {
                event.preventDefault();
                const statutBim = this.$store.getters['menuState/getStatutBim'];
                this.modaleBimMessage = statutBim.bimConnected
                    ? this.$t('bim.connecte.message')
                    : this.$t('bim.non.connecte.message');
                this.modaleBimOuverte = true;
            } else if (
                item.href === window.CONFIG.external.noe3Url ||
                item.href === window.CONFIG.external.noeEcoEnergieUrl ||
                item.href === this.sharepointLink
            ) {
                event.preventDefault();
                window.open(item.href);
            }
        },
    },
});
</script>

<style scoped>
.container {
    height: 100vh;
    padding-left: 65px;
}
.skip-link {
    position: absolute;
    left: -10000px;
    top: auto;
    width: 1px;
    height: 1px;
    overflow: hidden;
}
.skip-link:focus {
    position: static;
    width: auto;
    height: auto;
    background: white;
    padding: 10px;
    z-index: 100;
}
.content-dashboard {
    height: calc(100vh - 90px) !important;
}
.content {
    position: relative;
    display: flex;
    flex-direction: column;
    height: calc(100vh - 60px);
    overflow: auto;
    background-color: var(--background-principal);
}
@media screen and (min-width: 980px) {
    .container {
        overflow: auto;
    }
}
.backgroundGris {
    height: 100%;
    width: 100%;
    background-color: var(--blur);
    position: absolute;
    z-index: 8;
}
.actualites {
    position: absolute;
    left: 65px;
    top: 13vh;
    z-index: 1;
}
.v-sidebar-menu {
    --vsm-item-hover-bg: var(--bleu-secondaire);
    --vsm-mobile-item-bg: var(--bleu-prinicipal);
    --vsm-mobile-item-color: #424b54;
    --vsm-item-open-color: #424b54;
    --vsm-item-color: #424b54;
    --vsm-item-open-bg: var(--bleu-secondaire);
    --vsm-icon-active-bg: var(--bleu-secondaire);
    --vsm-icon-bg: var(--bleu-prinicipal);
    --vsm-dropdown-bg: var(--bleu-prinicipal);
}
.menu {
    box-shadow: 5px 4px 5px 0 rgba(0, 0, 0, 0.1);
}

.pin-button {
    background: var(--white);
    border: 1px solid var(--bleu-secondaire);
    border-radius: 50%;
    width: 30px;
    height: 30px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s;
}
.header-container {
    display: flex;
    opacity: 0;
    overflow: hidden;
    align-items: center;
    padding: 10px 10px 0;
    width: 100%;
}

.v-sidebar-menu.vsm_expanded .header-container {
    opacity: 1;
    transition: opacity 0.2s ease 0.25s;
}

.title-container {
    padding-top: 10px;
    color: var(--white);
    white-space: nowrap;
    overflow: hidden;
    font-weight: bold;
    flex-grow: 1;
    text-align: center;
}

.pin-container {
    width: 40px;
    display: flex;
    justify-content: flex-end;
}
</style>
